import React from 'react';
import { Check, Pencil, X } from 'lucide-react';
import {
  extractReservationTableIds,
  formatPeopleLabel,
  getPeopleCount,
} from '../../utils/reservationPeople.ts';

export type RoomSeatCapacityWarning = {
  title: string;
  message: string;
  confirmLabel: string;
} | null;

export type RoomSeatStatusMeta = {
  className: string;
  label: string;
  statLabel: string;
  statValue: string;
  statDetail?: string;
  sectionTitle: string;
  sectionDescription: string;
  actionLabel: string;
};

export type RoomSeatTableCapacity = {
  min: number;
  max: number;
  label: string;
};

type RoomSeatPanelProps = {
  selectedTable: any | null;
  isEditingSelectedTableName: boolean;
  selectedTableNameDraft: string;
  setSelectedTableNameDraft: (value: string) => void;
  selectedTableNameInputRef: React.RefObject<HTMLInputElement | null>;
  selectedTableNameSaving: boolean;
  onSaveSelectedTableName: () => void;
  onCancelSelectedTableNameEdit: () => void;
  onStartSelectedTableNameEdit: () => void;
  selectedTableStatusMeta: RoomSeatStatusMeta;
  selectedTableCapacity: RoomSeatTableCapacity;
  selectedTableOcupacao: any | null;
  selectedTableIsAwaitingGuest: boolean;
  selectedTableGuestCount: number | null;
  selectedTableReservation: any | null;
  selectedTableReservationActiveTableNames: string | null;
  selectedReservation: any | null;
  selectedTablePendingReservations: any[];
  actionError: string | null;
  seatCapacityWarning: RoomSeatCapacityWarning;
  seatCapacityWarningConfirmed: boolean;
  setSeatCapacityWarningConfirmed: (value: boolean) => void;
  actionLoadingId: string | null;
  onSeatAssignedSelected: () => void;
  onLeaveSelected: (options?: { releaseAll?: boolean }) => void;
  selectedTableCanLeaveAll: boolean;
  selectedTableLeaveAllLabel: string;
  selectedReservationId: string;
  onSelectReservation: (id: string) => void;
  availableReservationsForFloor: any[];
  seatedReservationIds: Set<string>;
  seatCount: number;
  setSeatCount: (value: number) => void;
  onSeatSelected: () => void;
};

const RoomSeatPanel = ({
  selectedTable,
  isEditingSelectedTableName,
  selectedTableNameDraft,
  setSelectedTableNameDraft,
  selectedTableNameInputRef,
  selectedTableNameSaving,
  onSaveSelectedTableName,
  onCancelSelectedTableNameEdit,
  onStartSelectedTableNameEdit,
  selectedTableStatusMeta,
  selectedTableCapacity,
  selectedTableOcupacao,
  selectedTableIsAwaitingGuest,
  selectedTableGuestCount,
  selectedTableReservation,
  selectedTableReservationActiveTableNames,
  selectedReservation,
  selectedTablePendingReservations,
  actionError,
  seatCapacityWarning,
  seatCapacityWarningConfirmed,
  setSeatCapacityWarningConfirmed,
  actionLoadingId,
  onSeatAssignedSelected,
  onLeaveSelected,
  selectedTableCanLeaveAll,
  selectedTableLeaveAllLabel,
  selectedReservationId,
  onSelectReservation,
  availableReservationsForFloor,
  seatedReservationIds,
  seatCount,
  setSeatCount,
  onSeatSelected,
}: RoomSeatPanelProps) => (
  <div className="room-seat-panel">
    {!selectedTable && (
      <div className="room-seat-empty">
        <strong>Selecione uma mesa no mapa</strong>
        <span>
          Escolha uma mesa para sentar visitantes, vincular reservas ou liberar uma ocupação atual.
        </span>
      </div>
    )}
    {selectedTable && (
      <div className="room-seat-content">
        <div className="room-seat-header">
          <div className="room-seat-header-top">
            <div className="room-seat-heading">
              {isEditingSelectedTableName ? (
                <div className="room-seat-name-edit-form">
                  <input
                    ref={selectedTableNameInputRef}
                    type="text"
                    className="room-seat-name-input"
                    value={selectedTableNameDraft}
                    onChange={(event) => setSelectedTableNameDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        onSaveSelectedTableName();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        onCancelSelectedTableNameEdit();
                      }
                    }}
                    maxLength={120}
                    disabled={selectedTableNameSaving}
                    aria-label="Nome da mesa"
                  />
                  <div className="room-seat-name-edit-actions">
                    <button
                      type="button"
                      className="room-seat-name-icon-button is-confirm"
                      onClick={onSaveSelectedTableName}
                      disabled={selectedTableNameSaving}
                      aria-label="Salvar nome da mesa"
                      title="Salvar nome da mesa"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      className="room-seat-name-icon-button is-cancel"
                      onClick={onCancelSelectedTableNameEdit}
                      disabled={selectedTableNameSaving}
                      aria-label="Cancelar edição do nome da mesa"
                      title="Cancelar edição"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="room-seat-name-row">
                  <div className="room-seat-name">{selectedTable.name}</div>
                  <button
                    type="button"
                    className="room-seat-name-icon-button"
                    onClick={onStartSelectedTableNameEdit}
                    aria-label="Editar nome da mesa"
                    title="Editar nome da mesa"
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              )}
            </div>
            <span className={`room-seat-status ${selectedTableStatusMeta.className}`}>
              {selectedTableStatusMeta.label}
            </span>
          </div>

          <div className="room-seat-stats">
            <div className="room-seat-stat-card">
              <span className="room-seat-stat-label">Capacidade</span>
              <strong className="room-seat-stat-value">{selectedTableCapacity.label}</strong>
            </div>

            <div className="room-seat-stat-card">
              <span className="room-seat-stat-label">
                {selectedTableStatusMeta.statLabel}
              </span>
              <strong className="room-seat-stat-value">
                {selectedTableStatusMeta.statValue}
              </strong>
              {selectedTableStatusMeta.statDetail && (
                <span className="room-seat-stat-detail">{selectedTableStatusMeta.statDetail}</span>
              )}
            </div>
          </div>
        </div>

        {selectedTableOcupacao && (
          <div className={`room-seat-section ${selectedTableIsAwaitingGuest ? 'room-seat-section-highlight' : ''}`}>
            <div className="room-seat-section-header">
              <div>
                <div className="room-seat-section-title">{selectedTableStatusMeta.sectionTitle}</div>
                <div className="room-seat-section-description">
                  {selectedTableStatusMeta.sectionDescription}
                </div>
              </div>
              <span className="room-seat-chip">
                {selectedTableGuestCount
                  ? formatPeopleLabel(selectedTableGuestCount)
                  : 'Sem contagem'}
              </span>
            </div>
            <div className="room-seat-section-emphasis">
              {selectedTableReservation?.name || selectedTableOcupacao?.nome_cliente || 'Visitante sem reserva'}
            </div>
            {selectedTableReservationActiveTableNames && (
              <div className="room-seat-section-note">
                Mesas da reserva: {selectedTableReservationActiveTableNames}
              </div>
            )}
            {(selectedTableReservation?.notes || selectedTableOcupacao?.obs) && (
              <div
                className="room-seat-observation"
                title={selectedTableReservation?.notes || selectedTableOcupacao?.obs}
              >
                <span className="room-seat-observation-label">Obs:</span>
                <span className="room-seat-observation-text">
                  {selectedTableReservation?.notes || selectedTableOcupacao?.obs}
                </span>
              </div>
            )}
          </div>
        )}

        {!selectedTableOcupacao && selectedReservation && (
          <div className="room-seat-section room-seat-section-highlight">
            <div className="room-seat-section-header">
              <div>
                <div className="room-seat-section-title">Reserva selecionada</div>
                <div className="room-seat-section-description">
                  {selectedReservation.time
                    ? `${String(selectedReservation.time).slice(0, 5)} • `
                    : ''}
                  {selectedReservation.name || `Reserva ${selectedReservation.id}`}
                </div>
              </div>
              <span className="room-seat-chip">
                {getPeopleCount(selectedReservation.guests)
                  ? formatPeopleLabel(getPeopleCount(selectedReservation.guests) || 0)
                  : 'Sem contagem'}
              </span>
            </div>
            {selectedReservation.notes && (
              <div
                className="room-seat-observation"
                title={selectedReservation.notes}
              >
                <span className="room-seat-observation-label">Obs:</span>
                <span className="room-seat-observation-text">
                  {selectedReservation.notes}
                </span>
              </div>
            )}
            {selectedReservation.tableId && (
              <div className="room-seat-section-note">
                {extractReservationTableIds(selectedReservation).some(
                  (tableId) => String(tableId) === String(selectedTable.id)
                )
                  ? 'Esta mesa já está vinculada a esta reserva.'
                  : `Reserva vinculada às mesas: ${selectedReservation.tableName || `Mesa ${selectedReservation.tableId}`}.`}
              </div>
            )}
          </div>
        )}

        {!selectedTableOcupacao && selectedTablePendingReservations.length > 0 && (
          <div className="room-seat-reservation-hint">
            <div className="room-seat-section-header">
              <div>
                <div className="room-seat-section-title">Reservas pendentes nesta mesa</div>
                <div className="room-seat-section-description">
                  Acompanhe as próximas reservas vinculadas a esta mesa.
                </div>
              </div>
              <span className="room-seat-chip is-muted">
                {selectedTablePendingReservations.length}
              </span>
            </div>
            <div className="room-seat-reservation-list">
              {selectedTablePendingReservations.slice(0, 2).map((reservation) => (
                <div key={reservation.id} className="room-seat-reservation-item">
                  <span className="room-seat-reservation-time">
                    {reservation.time ? String(reservation.time).slice(0, 5) : '--:--'}
                  </span>
                  <span className="room-seat-reservation-name">
                    {reservation.name || `Reserva ${reservation.id}`}
                  </span>
                </div>
              ))}
              {selectedTablePendingReservations.length > 2 && (
                <span className="room-seat-hint">
                  + {selectedTablePendingReservations.length - 2} reserva{selectedTablePendingReservations.length - 2 !== 1 ? 's' : ''} agendada{selectedTablePendingReservations.length - 2 !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        {actionError && (
          <div className="room-seat-error">{actionError}</div>
        )}

        {selectedTableOcupacao ? (
          <>
          {selectedTableIsAwaitingGuest && seatCapacityWarning && (
            <div className="reservation-modal-warning room-seat-capacity-warning" role="alert">
              <strong className="reservation-modal-warning-title">
                {seatCapacityWarning.title}
              </strong>
              <p className="reservation-modal-warning-text">
                {seatCapacityWarning.message}
              </p>
              <label className="reservation-modal-warning-check">
                <input
                  type="checkbox"
                  checked={seatCapacityWarningConfirmed}
                  onChange={(event) => setSeatCapacityWarningConfirmed(event.target.checked)}
                  disabled={actionLoadingId === String(selectedTable.id)}
                />
                <span>{seatCapacityWarning.confirmLabel}</span>
              </label>
            </div>
          )}
          <div className={`room-seat-actions ${selectedTableIsAwaitingGuest ? '' : 'room-seat-actions-compact'}`}>
            {selectedTableIsAwaitingGuest && (
              <button
                type="button"
                className="action-button room-seat-submit-button"
                onClick={onSeatAssignedSelected}
                disabled={
                  actionLoadingId === String(selectedTable.id)
                  || Boolean(seatCapacityWarning && !seatCapacityWarningConfirmed)
                }
              >
                <span>{actionLoadingId === String(selectedTable.id) ? 'Sentando...' : 'Sentar'}</span>
              </button>
            )}
            <button
              type="button"
              className="btn btn-danger room-seat-danger-button"
              onClick={() => onLeaveSelected()}
              disabled={actionLoadingId === String(selectedTable.id)}
            >
              {actionLoadingId === String(selectedTable.id) ? 'Liberando...' : selectedTableStatusMeta.actionLabel}
            </button>
            {selectedTableCanLeaveAll && (
              <button
                type="button"
                className="room-today-action is-secondary"
                onClick={() => onLeaveSelected({ releaseAll: true })}
                disabled={actionLoadingId === String(selectedTable.id)}
              >
                {actionLoadingId === String(selectedTable.id) ? 'Liberando...' : selectedTableLeaveAllLabel}
              </button>
            )}
          </div>
          </>
        ) : (
          <div className="room-seat-section room-seat-section-actions">
            <div className="room-seat-section-header">
              <div>
                <div className="room-seat-section-title">Sentar clientes</div>
                <div className="room-seat-section-description">
                  Associe uma reserva ou sente um visitante diretamente nesta mesa.
                </div>
              </div>
            </div>

            <div className="room-seat-field">
              <label className="form-label" htmlFor="reservation-select">
                Reserva
              </label>
              <select
                id="reservation-select"
                className="form-control"
                value={selectedReservationId}
                onChange={(e) => onSelectReservation(e.target.value)}
              >
                <option value="">Visitante (sem reserva)</option>
                {availableReservationsForFloor.map((res) => (
                  <option key={res.id} value={String(res.id)}>
                    {res.time} - {res.name || `Reserva ${res.id}`} ({res.guests}p)
                    {selectedTable && extractReservationTableIds(res).some(
                      (tableId) => String(tableId) === String(selectedTable.id)
                    )
                      ? ' - mesa desta reserva'
                      : ''}
                    {seatedReservationIds.has(String(res.id)) && res.tableName
                      ? ` - em uso: ${res.tableName}`
                      : ''}
                  </option>
                ))}
              </select>

            </div>

            <div className="room-seat-field">
              <label className="form-label" htmlFor="seat-count">
                Número de pessoas
              </label>
              <input
                id="seat-count"
                type="number"
                min={1}
                value={seatCount}
                onChange={(e) => setSeatCount(parseInt(e.target.value, 10) || 0)}
                className="form-control"
              />
              <span className="room-seat-hint">
                Referência desta mesa: {selectedTableCapacity.min} a {selectedTableCapacity.max} pessoas.
              </span>
            </div>

            {seatCapacityWarning && (
              <div className="reservation-modal-warning room-seat-capacity-warning" role="alert">
                <strong className="reservation-modal-warning-title">
                  {seatCapacityWarning.title}
                </strong>
                <p className="reservation-modal-warning-text">
                  {seatCapacityWarning.message}
                </p>
                <label className="reservation-modal-warning-check">
                  <input
                    type="checkbox"
                    checked={seatCapacityWarningConfirmed}
                    onChange={(event) => setSeatCapacityWarningConfirmed(event.target.checked)}
                    disabled={actionLoadingId === String(selectedTable.id)}
                  />
                  <span>{seatCapacityWarning.confirmLabel}</span>
                </label>
              </div>
            )}

            <div className="room-seat-actions">
              <button
                type="button"
                className="action-button room-seat-submit-button"
                onClick={onSeatSelected}
                disabled={
                  actionLoadingId === String(selectedTable.id)
                  || Boolean(seatCapacityWarning && !seatCapacityWarningConfirmed)
                }
              >
                <span>{actionLoadingId === String(selectedTable.id) ? 'Ocupando...' : 'Sentar'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    )}
  </div>

);

export default RoomSeatPanel;
